import { useEffect, useRef, useState } from "react";
import { useLocation, useRoute } from "wouter";
import AppShell from "@/components/foundations/core/AppShell/AppShell";
import { TaskColorDraft, TaskColorEditor } from "@/components/tasks/options/TaskColorEditor/TaskColorEditor";
import { trpc } from "@/lib/trpc";
import { DEFAULT_AREA_COLOR, ListColorKey, resolveTaskColors } from "@/lib/listColors";
import { toast } from "sonner";

export default function TaskColors() {
  const [, params] = useRoute<{ id: string }>("/task/:id/display");
  const [, setLocation] = useLocation();
  const id = params ? parseInt(params.id, 10) : 0;
  const utils = trpc.useUtils();
  const taskQuery = trpc.tasks.get.useQuery({ id }, { enabled: id > 0 });
  const [draft, setDraft] = useState<TaskColorDraft>({
    inheritView: true,
    barColor: null,
    bgColor: null,
  });
  const hydratedKey = useRef<number | null>(null);

  useEffect(() => {
    if (!taskQuery.data) return;
    if (hydratedKey.current === taskQuery.data.id) return;
    hydratedKey.current = taskQuery.data.id;
    const barColor = (taskQuery.data.barColor ?? null) as ListColorKey | null;
    const bgColor = (taskQuery.data.bgColor ?? null) as ListColorKey | null;
    setDraft({
      inheritView: barColor === null && bgColor === null,
      barColor,
      bgColor,
    });
  }, [taskQuery.data]);

  const updateMutation = trpc.tasks.update.useMutation({
    onSuccess: async (_data, vars) => {
      utils.tasks.get.setData({ id }, old =>
        old
          ? {
              ...old,
              barColor: (vars.barColor ?? null) as string | null,
              bgColor: (vars.bgColor ?? null) as string | null,
            }
          : old,
      );
      await Promise.all([
        utils.tasks.get.invalidate({ id }),
        utils.day.list.invalidate(),
        utils.day.monthSummary.invalidate(),
      ]);
      setLocation(`/task/${id}`);
    },
    onError: e => toast.error(e.message),
  });

  function saveColors() {
    updateMutation.mutate({
      id,
      barColor: draft.inheritView ? null : draft.barColor,
      bgColor: draft.inheritView ? null : draft.bgColor,
    });
  }

  if (!id) return null;
  if (taskQuery.isLoading || !taskQuery.data) {
    return (
      <AppShell backTo={`/task/${id}`} title="Display">
        <div className="container max-w-2xl py-8">
          <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />
        </div>
      </AppShell>
    );
  }

  const t = taskQuery.data;
  const fallbackBg = (t as { listBgColor?: string | null }).listBgColor ?? null;
  const statusColors = resolveTaskColors({
    barColor: draft.inheritView ? null : draft.barColor,
    bgColor: draft.inheritView ? null : draft.bgColor,
    listColor: (t.listColor as string | null) ?? DEFAULT_AREA_COLOR,
    listBgColor: fallbackBg,
  });

  return (
    <AppShell backTo={`/task/${id}`} title="Display">
      <div className="container max-w-2xl py-8 pb-12">
        <TaskColorEditor
          value={draft}
          onChange={setDraft}
          onCancel={() => setLocation(`/task/${id}`)}
          onSave={saveColors}
          isSaving={updateMutation.isPending}
          previewTitle={t.title}
          previewLabel={t.listName ?? "Inbox"}
          fallbackBar={(t.listColor as string | null) ?? DEFAULT_AREA_COLOR}
          fallbackBg={fallbackBg}
          accent={statusColors}
        />
      </div>
    </AppShell>
  );
}
